/**
 * Stock counts (opptelling) for the product stock drawer.
 *
 * Counts share `product.stockHistory` with purchases. Purchase entries are the untyped
 * `{ addedDate, qty, inPrice }` the drawer has always written; a count is typed so every
 * consumer of the history (drawer, Ammo-innkjøp table, backend/lib/club-ledger.js mirror)
 * can tell them apart. Keep the entry shape in sync with backend/lib/club-ledger.js.
 */
export const COUNT_ENTRY_TYPE = 'count';

const toNumber = (val) => {
    if (val == null || val === '') return 0;
    const n = Number(typeof val === 'string' ? val.replace(',', '.') : val);
    return Number.isNaN(n) ? 0 : n;
};

/** counted − expected: negative = gone without a registration (svinn), positive = over-registered. */
export function countDiff(counted, expected) {
    return toNumber(counted) - toNumber(expected);
}

/**
 * What a purchase costs the club: qty × per-unit inPrice, rounded like the backend.
 * Mirrors `round2(qty * inPrice)` in backend/lib/club-ledger.js, which is what the
 * ledger mirror stores as `amount` — so the drawer can show the same number BEFORE
 * saving, and a batch total typed into the per-unit field is obvious on sight.
 * Returns 0 unless both numbers are positive.
 */
export function purchaseTotal(qty, inPrice) {
    const qtyNum = toNumber(qty);
    const priceNum = toNumber(inPrice);
    if (!(qtyNum > 0) || !(priceNum > 0)) {
        return 0;
    }
    return Math.round(((qtyNum * priceNum) + Number.EPSILON) * 100) / 100;
}

export function buildCountEntry({
    counted, expected, userId, now = new Date(),
}) {
    const countedNum = toNumber(counted);
    const expectedNum = toNumber(expected);
    const diff = countedNum - expectedNum;
    return {
        type: COUNT_ENTRY_TYPE,
        addedDate: now.toISOString(),
        counted: countedNum,
        expected: expectedNum,
        diff,
        qty: diff,
        user: userId,
    };
}

export function isCountEntry(entry) {
    return !!entry && entry.type === COUNT_ENTRY_TYPE;
}

export function isPurchaseEntry(entry) {
    return !!entry && (!entry.type || entry.type === 'purchase');
}

function lastMatching(history, predicate) {
    const list = Array.isArray(history) ? history : [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
        if (predicate(list[i])) {
            return list[i];
        }
    }
    return null;
}

export function lastPurchaseEntry(history) {
    return lastMatching(history, isPurchaseEntry);
}

export function lastCountEntry(history) {
    return lastMatching(history, isCountEntry);
}

/**
 * Series for the count-history plot in the stock drawer: one point per count, oldest
 * first, with `expected` (dashed) and `counted` (solid) so the gap between the lines
 * is the svinn at each count. `counts` is the log from /api/ammosales/stock-movements
 * (newest first). xLabels are the point indexes, xTickLabels the formatted dates.
 */
export function buildCountSeries(counts, { formatDate = v => v, expectedLabel = 'Expected', countedLabel = 'Counted' } = {}) {
    const ordered = (Array.isArray(counts) ? [...counts] : [])
        .sort((a, b) => new Date(a.addedDate) - new Date(b.addedDate));
    const xLabels = ordered.map((_, i) => i);
    const xTickLabels = ordered.map(c => formatDate(c.addedDate));
    const series = [
        {
            label: expectedLabel,
            color: 'var(--bs-secondary)',
            dash: true,
            points: ordered.map((c, i) => ({ x: i, y: Math.max(0, toNumber(c.expected)) })),
        },
        {
            label: countedLabel,
            color: 'var(--bs-primary)',
            points: ordered.map((c, i) => ({ x: i, y: Math.max(0, toNumber(c.counted)) })),
        },
    ];
    return { xLabels, xTickLabels, series };
}
